/**
 * Keep in sync with server/constants_and_utils/patients/labResults.js (structured lab plaintext helpers).
 */

export const LAB_OBJECT_NAMES = {
  BASIC_CHEMISTRY: 'Basic Chemistry',
  COMPLETE_BLOOD_COUNT: 'Complete Blood Count',
};

const BASIC_CHEMISTRY_ALIASES = new Set([
  'basic chemistry',
  'chem 7',
  'chem7',
  'chem-7',
]);

const COMPLETE_BLOOD_COUNT_ALIASES = new Set([
  'complete blood count',
  'cbc',
]);

function safeNum(val, decimals = 2) {
  if (val === null || val === undefined) return '';
  const n = Number(val);
  if (!Number.isFinite(n)) return '';
  if (decimals === 0) return String(Math.round(n));
  return Number(n).toFixed(decimals);
}

export function parseResult(result) {
  if (result === null || result === undefined) return null;
  if (typeof result === 'object' && !Array.isArray(result)) return result;
  if (typeof result !== 'string') return null;
  const s = result.trim();
  if (!s) return null;
  try {
    const parsed = JSON.parse(s);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** @returns {'basic_chemistry'|'complete_blood_count'|null} */
export function resolveStructuredLabKind(objectName) {
  const n = String(objectName || '').trim().toLowerCase();
  if (!n) return null;
  if (BASIC_CHEMISTRY_ALIASES.has(n)) return 'basic_chemistry';
  if (COMPLETE_BLOOD_COUNT_ALIASES.has(n)) return 'complete_blood_count';
  return null;
}

export function formatBasicChemistryPlaintext(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const s = obj.sodium;
  const p = obj.potassium;
  const c = obj.chloride;
  const b = obj.bicarbonate;
  const bun = obj.bun;
  const cr = obj.creatinine;
  const g = obj.glucose_non_fasting;
  const parts = [];
  if (s !== undefined && s !== null && s !== '') parts.push(`Sodium: ${safeNum(s, 0)} mEq/L`);
  if (p !== undefined && p !== null && p !== '') parts.push(`Potassium: ${safeNum(p, 1)} mEq/L`);
  if (c !== undefined && c !== null && c !== '') parts.push(`Chloride: ${safeNum(c, 0)} mEq/L`);
  if (b !== undefined && b !== null && b !== '') parts.push(`Bicarbonate: ${safeNum(b, 0)} mEq/L`);
  if (bun !== undefined && bun !== null && bun !== '') parts.push(`BUN: ${safeNum(bun, 0)} mg/dL`);
  if (cr !== undefined && cr !== null && cr !== '') parts.push(`Creatinine: ${safeNum(cr, 2)} mg/dL`);
  if (g !== undefined && g !== null && g !== '') parts.push(`Glucose: ${safeNum(g, 0)} mg/dL (non-fasting)`);
  return parts.join(', ');
}

export function formatCompleteBloodCountPlaintext(obj) {
  if (!obj || typeof obj !== 'object') return '';
  const parts = [];
  if (obj.hb !== undefined && obj.hb !== null && obj.hb !== '') parts.push(`Hemoglobin: ${safeNum(obj.hb, 1)} g/dL`);
  const wbc = obj.wbc;
  const wbcCount = wbc && (wbc.count !== undefined && wbc.count !== null && wbc.count !== '')
    ? wbc.count
    : (obj.wbc_count !== undefined && obj.wbc_count !== null && obj.wbc_count !== '' ? obj.wbc_count : null);
  if (wbcCount !== null) parts.push(`White blood cell count: ${safeNum(wbcCount, 0)}/μL`);
  if (wbc && typeof wbc === 'object') {
    if (wbc.neutrophils !== undefined && wbc.neutrophils !== null && wbc.neutrophils !== '') parts.push(`Neutrophils: ${safeNum(wbc.neutrophils, 1)}%`);
    if (wbc.lymphocytes !== undefined && wbc.lymphocytes !== null && wbc.lymphocytes !== '') parts.push(`Lymphocytes: ${safeNum(wbc.lymphocytes, 1)}%`);
    if (wbc.monocytes !== undefined && wbc.monocytes !== null && wbc.monocytes !== '') parts.push(`Monocytes: ${safeNum(wbc.monocytes, 1)}%`);
    if (wbc.basophils !== undefined && wbc.basophils !== null && wbc.basophils !== '') parts.push(`Basophils: ${safeNum(wbc.basophils, 1)}%`);
    if (wbc.eosinophils !== undefined && wbc.eosinophils !== null && wbc.eosinophils !== '') parts.push(`Eosinophils: ${safeNum(wbc.eosinophils, 1)}%`);
  }
  if (obj.rbc !== undefined && obj.rbc !== null && obj.rbc !== '') parts.push(`Erythrocyte count (RBC): ${safeNum(obj.rbc, 1)} million/μL`);
  if (obj.hct !== undefined && obj.hct !== null && obj.hct !== '') parts.push(`Hematocrit: ${safeNum(obj.hct, 0)}%`);
  if (obj.mcv !== undefined && obj.mcv !== null && obj.mcv !== '') parts.push(`Mean corpuscular volume: ${safeNum(obj.mcv, 0)} fL`);
  if (obj.mch !== undefined && obj.mch !== null && obj.mch !== '') parts.push(`Mean corpuscular hemoglobin: ${safeNum(obj.mch, 0)} pg`);
  if (obj.mchc !== undefined && obj.mchc !== null && obj.mchc !== '') parts.push(`Mean corpuscular hemoglobin concentration: ${safeNum(obj.mchc, 1)} g/dL`);
  if (obj.platelet_count !== undefined && obj.platelet_count !== null && obj.platelet_count !== '') parts.push(`Platelet count: ${safeNum(obj.platelet_count, 0)}/μL`);
  return parts.join(', ');
}

export function translateLabResultToPlaintext(objectName, result) {
  const kind = resolveStructuredLabKind(objectName);
  if (kind === 'basic_chemistry') {
    const obj = parseResult(result);
    if (obj) return formatBasicChemistryPlaintext(obj);
    return typeof result === 'string' ? result : '';
  }
  if (kind === 'complete_blood_count') {
    const obj = parseResult(result);
    if (obj) return formatCompleteBloodCountPlaintext(obj);
    return typeof result === 'string' ? result : '';
  }
  return result === undefined || result === null ? '' : String(result);
}
